import { QueryClient } from '@tanstack/react-query';
import type { StateManagerConfig } from '../types';

// Default query configuration
const defaultQueryConfig = {
  staleTime: 5 * 60 * 1000, // 5 minutes
  cacheTime: 10 * 60 * 1000, // 10 minutes
  refetchOnWindowFocus: false,
  networkMode: 'offlineFirst' as const, // Prevents duplicate requests
  retry: (failureCount: number, error: unknown) => {
    // Don't retry on authentication errors
    if (error && typeof error === 'object' && 'status' in error) {
      const status = (error as { status: number }).status;
      if (status === 401 || status === 403) {
        return false;
      }
    }

    // Retry up to 2 times for other errors
    return failureCount < 2;
  },
  retryDelay: (attemptIndex: number) =>
    Math.min(1000 * 2 ** attemptIndex, 30000),
};

export function createQueryClient(config?: StateManagerConfig): QueryClient {
  const queryDefaults = config?.queryDefaults || {};

  return new QueryClient({
    defaultOptions: {
      queries: {
        ...defaultQueryConfig,
        ...queryDefaults,
      },
      mutations: {
        retry: false,
        // Global error handling for mutations
        onError: error => {
          console.error('Mutation error:', error);
        },
      },
    },
  });
}

// Singleton query client
let queryClientInstance: QueryClient | null = null;

export function getQueryClient(config?: StateManagerConfig): QueryClient {
  if (!queryClientInstance) {
    queryClientInstance = createQueryClient(config);
  }
  return queryClientInstance;
}
